import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanFace, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmProvider";

const ago = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");

// Admin/HR dashboard: who hasn't enrolled Face ID yet, with a one-tap reminder.
export default function FaceEnrollmentCard() {
    const [data, setData] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [sending, setSending] = useState(false);
    const { confirm } = useConfirm();

    const load = () => api.get("/face-enrollment/pending").then(({ data: d }) => setData(d)).catch(() => setData(null));
    useEffect(() => { load(); }, []);

    if (!data || data.pending.length === 0) return null;
    const rows = showAll ? data.pending : data.pending.slice(0, 6);

    const remind = async () => {
        const ok = await confirm({
            title: "Send face-enrollment reminders?",
            description: `${data.pending.length} staff will get a phone alert, email and chat message. Anyone reminded in the last 12 hours is skipped.`,
            confirmLabel: "Remind all",
        });
        if (!ok) return;
        setSending(true);
        try {
            const { data: r } = await api.post("/face-enrollment/remind");
            toast.success(r.reminded
                ? `Reminded ${r.reminded} staff · ${r.pushed} phone alerts · ${r.emailed} emails · ${r.chatted} chat messages${r.skipped_recent ? ` · ${r.skipped_recent} skipped (reminded recently)` : ""}`
                : "Everyone was reminded in the last 12 hours — try again later");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send reminders");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="mt-8 bg-white border border-ink/10 p-6 sm:p-8" data-testid="face-enroll-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><ScanFace className="w-6 h-6" /></div>
                    <div>
                        <div className="overline">Face ID</div>
                        <div className="mt-1 font-display font-bold text-xl text-ink" data-testid="face-enroll-count">
                            {data.pending.length} of {data.total_active} staff haven&apos;t enrolled
                        </div>
                        <p className="text-sm text-ink/55">They can&apos;t use face check-in until they do.</p>
                    </div>
                </div>
                <button onClick={remind} disabled={sending} className="inline-flex items-center gap-2 bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="face-enroll-remind-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} Remind all
                </button>
            </div>
            <div className="mt-5 divide-y divide-ink/5 border-t border-ink/10" data-testid="face-enroll-list">
                {rows.map((r) => (
                    <Link key={r.id} to={`/employees/${r.id}`} className="flex items-center justify-between gap-3 py-2.5 hover:bg-ink/[0.02]" data-testid={`face-enroll-row-${r.id}`}>
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-ink truncate">{r.name} <span className="text-ink/40 font-normal">{r.employee_no}</span></div>
                            <div className="text-xs text-ink/50 truncate">{r.department || "No department"}{!r.has_account && " · no app account yet"}</div>
                        </div>
                        {r.reminded_at && <span className="text-[11px] text-ink/45 shrink-0">Reminded {ago(r.reminded_at)}</span>}
                    </Link>
                ))}
            </div>
            {data.pending.length > 6 && (
                <button onClick={() => setShowAll((v) => !v)} className="mt-3 text-xs text-klein hover:underline" data-testid="face-enroll-toggle">
                    {showAll ? "Show less" : `Show all ${data.pending.length}`}
                </button>
            )}
        </div>
    );
}
